import * as tslib_1 from "tslib";
/** @jsx jsx */

import { jsx, css } from "@emotion/core";
import * as React from "react";
import PropTypes from "prop-types";
import { useTheme } from "./Theme/Providers";
export var getVariantStyles = function getVariantStyles(theme, variant) {
  var _a, _b, _c;

  switch (variant) {
    case "uppercase":
      return css({
        textTransform: "uppercase",
        fontSize: theme.fontSizes[0],
        fontWeight: 600,
        color: theme.colors.text.muted,
        letterSpacing: "-0.02em"
      });

    case "body":
      return css({
        fontSize: theme.fontSizes[1]
      });

    case "paragraph":
      return css({
        fontSize: theme.fontSizes[1],
        marginBottom: theme.spaces.md
      });

    case "subtitle":
      return css({
        fontSize: theme.fontSizes[0],
        fontWeight: 400,
        color: theme.colors.text.muted,
        lineHeight: theme.lineHeights.body
      });

    case "lead":
      return css({
        fontWeight: 400,
        fontSize: theme.fontSizes[2]
      });

    case "h6":
      return css({
        color: theme.colors.text.heading,
        fontWeight: theme.fontWeights.heading,
        lineHeight: 1.5,
        fontSize: theme.fontSizes[1],
        marginBottom: theme.spaces.sm
      });

    case "h5":
      return css({
        color: theme.colors.text.heading,
        fontWeight: theme.fontWeights.heading,
        fontSize: theme.fontSizes[2],
        lineHeight: theme.lineHeights.heading,
        marginBottom: theme.spaces.sm
      });

    case "h4":
      return css({
        color: theme.colors.text.heading,
        fontWeight: theme.fontWeights.heading,
        fontSize: theme.fontSizes[3],
        lineHeight: theme.lineHeights.heading,
        marginBottom: theme.spaces.sm,
        letterSpacing: "-0.2px"
      });

    case "h3":
      return css({
        color: theme.colors.text.heading,
        fontWeight: theme.fontWeights.heading,
        fontSize: theme.fontSizes[4],
        lineHeight: theme.lineHeights.heading,
        marginBottom: theme.spaces.sm
      });

    case "h2":
      return css({
        color: theme.colors.text.heading,
        fontWeight: theme.fontWeights.heading,
        fontSize: theme.fontSizes[5],
        lineHeight: theme.lineHeights.heading,
        marginBottom: theme.spaces.sm,
        letterSpacing: "-0.2px"
      });

    case "h1":
      return css({
        color: theme.colors.text.heading,
        fontWeight: theme.fontWeights.heading,
        fontSize: theme.fontSizes[6],
        lineHeight: theme.lineHeights.heading,
        marginBottom: theme.spaces.sm,
        letterSpacing: "-0.2px"
      });

    case "display3":
      return css((_a = {
        color: theme.colors.text.heading,
        fontWeight: theme.fontWeights.display,
        fontSize: theme.fontSizes[5],
        lineHeight: theme.lineHeights.heading,
        marginBottom: theme.spaces.sm
      }, _a[theme.mediaQueries.md] = {
        fontSize: theme.fontSizes[6]
      }, _a));

    case "display2":
      return css((_b = {
        color: theme.colors.text.heading,
        fontWeight: theme.fontWeights.display,
        fontSize: theme.fontSizes[6],
        lineHeight: theme.lineHeights.heading,
        marginBottom: theme.spaces.sm
      }, _b[theme.mediaQueries.md] = {
        fontSize: theme.fontSizes[7]
      }, _b));

    case "display1":
      return css((_c = {
        color: theme.colors.text.heading,
        fontWeight: theme.fontWeights.display,
        fontSize: theme.fontSizes[7],
        lineHeight: theme.lineHeights.heading,
        marginBottom: theme.spaces.sm
      }, _c[theme.mediaQueries.md] = {
        fontSize: theme.fontSizes[8]
      }, _c));
  }
};
var element = {
  display1: "h1",
  display2: "h1",
  display3: "h1",
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  uppercase: "div",
  subtitle: "div",
  body: "span",
  paragraph: "p",
  hidden: "span",
  lead: "p"
};

var basicStyles = function basicStyles(theme) {
  return {
    base: css({
      boxSizing: "border-box",
      margin: 0,
      fontWeight: theme.fontWeights.body,
      lineHeight: theme.lineHeights.body,
      fontFamily: theme.fonts.base,
      fontSize: theme.fontSizes[1],
      color: theme.colors.text["default"],
      WebkitFontSmoothing: "antialiased",
      WebkitTextSizeAdjust: "none"
    }),
    noGutter: css({
      marginBottom: 0
    }),
    noWrap: css({
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    }),
    muted: css({
      color: theme.colors.text.muted
    })
  };
};

export var Text = function Text(_a) {
  var _b = _a.variant,
      variant = _b === void 0 ? "body" : _b,
      _c = _a.wrap,
      wrap = _c === void 0 ? true : _c,
      _d = _a.gutter,
      gutter = _d === void 0 ? true : _d,
      muted = _a.muted,
      component = _a.component,
      other = tslib_1.__rest(_a, ["variant", "wrap", "gutter", "muted", "component"]);

  var Component = component || element[variant];
  var theme = useTheme();
  var variantStyles = React.useMemo(function () {
    return getVariantStyles(theme, variant);
  }, [theme, variant]);
  var styles = React.useMemo(function () {
    return basicStyles(theme);
  }, [theme]);
  return jsx(Component, tslib_1.__assign({
    css: [styles.base, !wrap && styles.noWrap, muted && styles.muted, variantStyles, !gutter && styles.noGutter]
  }, other));
};
Text.propTypes = {
  variant: PropTypes.oneOf(["uppercase", "hidden", "body", "paragraph", "subtitle", "lead", "h6", "h5", "h4", "h3", "h2", "h1", "display3", "display2", "display1"]),
  wrap: PropTypes.bool,
  muted: PropTypes.bool,
  gutter: PropTypes.bool,
  component: PropTypes.elementType
};